import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Camera, Image, Video, Square, Circle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import StoryViewer, { mediaSrc } from './stories/StoryViewer';

const StoriesMoments = () => {
  const { user, api } = useAuth();
  const [stories, setStories] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newStoryText, setNewStoryText] = useState('');
  const [newStoryImage, setNewStoryImage] = useState(null);
  const [newStoryVideo, setNewStoryVideo] = useState(null);
  const [mediaType, setMediaType] = useState('text');
  const [cameraStream, setCameraStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const MAX_RECORD_SECONDS = 30;

  useEffect(() => { fetchStories(); }, []);

  const fetchStories = async () => {
    try {
      const response = await api.get('/stories');
      const normalized = (response.data || []).map((s) => ({
        ...s,
        slides: s.slides || [{
          type: s.video_url ? 'video' : (s.image_url ? 'image' : 'text'),
          content: s.content,
          image_url: s.image_url,
          video_url: s.video_url,
        }],
      }));
      setStories(normalized);
    } catch (error) {
      setStories([]);
    }
  };

  // Upload media file to backend
  const uploadMedia = async (blob, filename) => {
    const formData = new FormData();
    formData.append('file', blob, filename);
    const response = await api.post('/stories/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  };

  const handleCreateStory = async () => {
    if (!newStoryText.trim() && !newStoryImage && !newStoryVideo && !recordedBlob) {
      toast.error('Ajoute du contenu a ton moment');
      return;
    }

    try {
      setUploading(true);
      let imageUrl = null;
      let videoUrl = null;

      // Upload recorded video
      if (recordedBlob) {
        const result = await uploadMedia(recordedBlob, 'story-video.webm');
        videoUrl = result.url;
      } else if (newStoryVideo && newStoryVideo.startsWith('blob:')) {
        // Video from file input as blob URL - re-fetch and upload
        const resp = await fetch(newStoryVideo);
        const blob = await resp.blob();
        const result = await uploadMedia(blob, 'story-video.mp4');
        videoUrl = result.url;
      } else if (newStoryVideo) {
        videoUrl = newStoryVideo;
      }

      if (newStoryImage && newStoryImage.startsWith('data:')) {
        // Base64 image - upload as file
        const resp = await fetch(newStoryImage);
        const blob = await resp.blob();
        const result = await uploadMedia(blob, 'story-image.jpg');
        imageUrl = result.url;
      } else if (newStoryImage) {
        imageUrl = newStoryImage;
      }

      await api.post('/stories', {
        content: newStoryText,
        image_url: imageUrl,
        video_url: videoUrl
      });

      toast.success('Moment partage !');
      resetCreateModal();
      fetchStories();
    } catch (error) {
      console.error('Error creating story:', error);
      toast.error('Erreur lors de la publication');
    } finally {
      setUploading(false);
    }
  };

  const resetCreateModal = () => {
    setShowCreateModal(false);
    setNewStoryText('');
    setNewStoryImage(null);
    setNewStoryVideo(null);
    setMediaType('text');
    setRecordedBlob(null);
    setRecordingTime(0);
    stopCamera();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop grande (max 5 Mo)'); return; }
    const reader = new FileReader();
    reader.onloadend = () => { setNewStoryImage(reader.result); setNewStoryVideo(null); setRecordedBlob(null); setMediaType('image'); };
    reader.readAsDataURL(file);
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('Video trop grande (max 50 Mo)'); return; }
    const url = URL.createObjectURL(file);
    setNewStoryVideo(url);
    setNewStoryImage(null);
    setRecordedBlob(null);
    setMediaType('video');
  };

  // Camera controls
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: true
      });
      setCameraStream(stream);
      setMediaType('camera');
      setRecordedBlob(null);
      setNewStoryImage(null);
      setNewStoryVideo(null);
    } catch (error) {
      if (error.name === 'NotAllowedError') toast.error('Acces camera refuse. Autorise la camera dans ton navigateur.');
      else if (error.name === 'NotFoundError') toast.error('Aucune camera detectee');
      else toast.error("Impossible d'acceder a la camera");
    }
  };

  useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(console.error);
    }
  }, [cameraStream]);

  const stopCamera = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  }, [cameraStream]);

  // Photo capture
  const capturePhoto = () => {
    if (videoRef.current && cameraStream) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      setNewStoryImage(canvas.toDataURL('image/jpeg', 0.8));
      setMediaType('image');
      stopCamera();
    }
  };

  // Video recording
  const startRecording = () => {
    if (!cameraStream) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';

    const recorder = new MediaRecorder(cameraStream, { mimeType });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setRecordedBlob(blob);
      setNewStoryVideo(URL.createObjectURL(blob));
      setMediaType('video-preview');
      stopCamera();
    };
    recorder.start(100);
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
    setRecordingTime(0);

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= MAX_RECORD_SECONDS - 1) {
          stopRecording();
          return MAX_RECORD_SECONDS;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  useEffect(() => {
    if (!showCreateModal) { stopCamera(); setRecordedBlob(null); }
  }, [showCreateModal, stopCamera]);

  // Story viewer
  const openStory = (index) => setActiveIndex(index);
  const closeStory = () => setActiveIndex(null);

  const handleStoryUpdate = (storyId, patch) => {
    setStories((prev) => prev.map((s) => (s.id === storyId ? { ...s, ...patch } : s)));
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <>
      {/* Stories Row */}
      <div className="flex items-center gap-3 p-4 overflow-x-auto scrollbar-hide">
        <button onClick={() => setShowCreateModal(true)} className="flex flex-col items-center gap-1 shrink-0" data-testid="create-story-btn">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-dashed border-primary/50 flex items-center justify-center hover:border-primary transition-colors">
            <Plus className="w-6 h-6 text-primary" />
          </div>
          <span className="text-xs text-muted-foreground">Ton moment</span>
        </button>

        {stories.map((story, index) => (
          <button key={story.id} onClick={() => openStory(index)} className="flex flex-col items-center gap-1 shrink-0" data-testid={`story-${story.id}`}>
            <div className={`p-0.5 rounded-full ${story.viewed ? 'bg-gray-400' : 'bg-gradient-to-r from-primary via-cyan-500 to-primary'}`}>
              <div className="p-0.5 bg-background rounded-full">
                <Avatar className="w-14 h-14 border-2 border-background">
                  <AvatarImage src={mediaSrc(story.user_avatar)} />
                  <AvatarFallback className="bg-secondary text-xs">{story.user_name?.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                </Avatar>
              </div>
            </div>
            <span className="text-xs text-muted-foreground max-w-[60px] truncate">{story.user_id === user?.id ? 'Toi' : story.user_name?.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      {/* Story Viewer */}
      <AnimatePresence>
        {activeIndex !== null && stories[activeIndex] && (
          <StoryViewer
            stories={stories}
            startIndex={activeIndex}
            onClose={closeStory}
            api={api}
            currentUserId={user?.id}
            onStoryUpdate={handleStoryUpdate}
          />
        )}
      </AnimatePresence>


      {/* Create Story Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center"
            onClick={resetCreateModal}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Partager un moment</h3>
                <Button variant="ghost" size="icon" onClick={resetCreateModal}><X className="w-5 h-5" /></Button>
              </div>

              {/* Preview Area */}
              <div className="aspect-[9/16] max-h-[300px] bg-gradient-to-br from-primary/80 to-cyan-600/80 rounded-xl mb-4 flex items-center justify-center p-6 relative overflow-hidden" data-testid="story-preview">
                {mediaType === 'camera' && cameraStream ? (
                  <>
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover rounded-lg" />
                    {/* Recording Overlay */}
                    {isRecording && (
                      <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-10">
                        <div className="flex items-center gap-2 bg-black/60 rounded-full px-3 py-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-white text-sm font-mono font-bold">{formatTime(recordingTime)}</span>
                        </div>
                        <div className="bg-black/60 rounded-full px-3 py-1.5">
                          <span className="text-white/70 text-xs">max {MAX_RECORD_SECONDS}s</span>
                        </div>
                      </div>
                    )}
                    {/* Recording progress bar */}
                    {isRecording && (
                      <div className="absolute bottom-16 left-3 right-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                        <div className="h-full bg-red-500 rounded-full transition-all duration-1000" style={{ width: `${(recordingTime / MAX_RECORD_SECONDS) * 100}%` }} />
                      </div>
                    )}
                    {/* Camera action buttons */}
                    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-6 z-10">
                      {!isRecording ? (
                        <>
                          <Button size="icon" className="rounded-full w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm" onClick={capturePhoto} title="Prendre une photo" data-testid="capture-photo-btn">
                            <Camera className="w-5 h-5 text-white" />
                          </Button>
                          <Button size="icon" className="rounded-full w-16 h-16 bg-red-500 hover:bg-red-600 ring-4 ring-white/30" onClick={startRecording} title="Enregistrer une video" data-testid="start-recording-btn">
                            <Circle className="w-7 h-7 text-white fill-white" />
                          </Button>
                          <Button size="icon" className="rounded-full w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm" onClick={stopCamera} title="Fermer la camera">
                            <X className="w-5 h-5 text-white" />
                          </Button>
                        </>
                      ) : (
                        <Button size="icon" className="rounded-full w-16 h-16 bg-red-500 hover:bg-red-600 ring-4 ring-white/30 animate-pulse" onClick={stopRecording} data-testid="stop-recording-btn">
                          <Square className="w-6 h-6 text-white fill-white" />
                        </Button>
                      )}
                    </div>
                  </>
                ) : mediaType === 'video-preview' && newStoryVideo ? (
                  <video src={newStoryVideo} controls playsInline className="max-w-full max-h-full object-contain rounded" />
                ) : mediaType === 'video' && newStoryVideo ? (
                  <video src={newStoryVideo} controls playsInline className="max-w-full max-h-full object-contain rounded" />
                ) : newStoryImage ? (
                  <img src={newStoryImage} alt="" className="max-w-full max-h-full object-contain rounded" />
                ) : (
                  <p className="text-white text-xl font-bold text-center">{newStoryText || "Ton message ici..."}</p>
                )}
              </div>

              {/* Text input - hide during camera/recording */}
              {mediaType !== 'camera' && (
                <Input placeholder="Ecris ton moment..." value={newStoryText} onChange={(e) => setNewStoryText(e.target.value)} className="mb-4" data-testid="story-text-input" />
              )}

              {/* Media Actions */}
              <div className="flex gap-2 mb-3">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
                <Button variant={newStoryImage ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => fileInputRef.current?.click()} data-testid="story-photo-btn">
                  <Image className="w-4 h-4 mr-1" /> Photo
                </Button>
                <Button variant={mediaType === 'video' || mediaType === 'video-preview' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => videoInputRef.current?.click()} data-testid="story-video-upload-btn">
                  <Video className="w-4 h-4 mr-1" /> Video
                </Button>
                <Button variant={mediaType === 'camera' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={mediaType === 'camera' ? stopCamera : startCamera} data-testid="story-camera-btn">
                  <Camera className="w-4 h-4 mr-1" /> {mediaType === 'camera' ? 'Stop' : 'Camera'}
                </Button>
              </div>

              {/* Publish button - hide during camera mode */}
              {mediaType !== 'camera' && (
                <Button className="w-full bg-gradient-to-r from-primary to-cyan-600" onClick={handleCreateStory} disabled={uploading} data-testid="publish-story-btn">
                  {uploading ? 'Publication en cours...' : 'Publier'}
                </Button>
              )}

              <p className="text-xs text-muted-foreground text-center mt-3">Visible pendant 24h par la communaute</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StoriesMoments;
